import { C } from '@parsina/framework/clinical/src/constants/theme';
import { Avatar, Button, Card, IconButton } from '@parsina/framework';
import StatusChip from '@parsina/framework/clinical/src/components/common/StatusChip';
import { statusColors, CT, soft } from '@parsina/framework/tokens';
import { CalendarClock, Clock, Play } from 'lucide-react';
import { waitedMinutes, visitOverrun } from '../../hooks/useReception';
import { URGENCY_BADGE, WAIT_WARN_MIN, formatDuration, isOwnPendingRequest } from '../../lib/appointments';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { useReceptionStrings } from '../../lib/receptionStrings';

// One appointment as a tappable mobile card (mobile concept §5): name + status
// badge, then time / urgency / wait / overrun / queue-position chips, and an
// «ادامه ویزیت» action when the visit is open. The whole card taps into the
// patient record; Enter does the same from the keyboard.

const OVERRUN_CHIP = {
  normal:   { bg: statusColors.in_visit.bg, color: statusColors.in_visit.text },
  over:     { bg: C.amberLight, color: CT.warnInk },
  critical: { bg: C.redLight,  color: CT.dangerInk },
};

const NEUTRAL_BG = CT.surface2;

const chipStyle = (bg, color) => ({
  fontSize: '0.625rem', padding: '0.125rem 0.5rem', borderRadius: '0.625rem',
  fontWeight: 700, background: bg, color,
});

const ClockGlyph = () => (
  <Clock size={11} strokeWidth={2.4} style={{ display: 'inline-block', verticalAlign: '-0.09375em', marginInlineEnd: '0.1875rem' }} aria-hidden />
);

const posLabel = (item, pos) => (pos === 1
  ? item('dashboard.queue.positionNext')
  : item('dashboard.queue.position', { position: pos }));

// `clinicName` is set only while پذیرش is showing every clinic at once — see the
// note on the desktop `AppointmentRow`.
const MobileAppointmentCard = ({ apt, queuePos, nowMs, onOpen, onContinue, onWithdraw, onReschedule, testId, clinicName }) => {
  const { t } = useTranslation();
  const s = useReceptionStrings();
  const waited = apt.status === 'arrived' ? waitedMinutes(apt, nowMs) : null;
  const { elapsedMin, level } = apt.status === 'in_visit'
    ? visitOverrun(apt, nowMs)
    : { elapsedMin: null, level: 'normal' };
  const urg = URGENCY_BADGE[apt.urgency];
  const overChip = OVERRUN_CHIP[level];

  return (
    // The card carries a nested action («ادامه ویزیت»), so it cannot itself be a
    // button — nesting one inside another is invalid. It stays the ARIA link
    // pattern: focusable, Enter-activated, with the kit Card as its surface.
    <div
      data-testid={testId}
      role="link"
      tabIndex={0}
      aria-label={t('dashboard.queue.patientRecord', {
        name: apt.name || apt.patient?.name || t('common.labels.patient'),
      })}
      onClick={onOpen}
      onKeyDown={e => { if (e.key === 'Enter') onOpen?.(); }}
      className="outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl cursor-pointer"
    >
      <Card style={{ padding: '0.875rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Avatar name={apt.name || apt.patient?.name || t('common.punct.question')} size={44} bg={apt.status === 'in_visit' ? C.teal : C.navy} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {apt.name || apt.patient?.name || '—'}
              </div>
              {/* StatusChip — see the note in components/AppointmentRow.jsx:
                  the framework badge's label is Persian in every language. */}
              <StatusChip status={apt.status} style={{ flexShrink: 0 }} />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.75rem', color: C.textMuted }}><ClockGlyph />{apt.time}</span>
              {urg && (
                <span style={chipStyle(urg.bg, urg.color)}>{t(urg.labelKey)}</span>
              )}
              {waited != null && (
                <span style={chipStyle(
                  waited > WAIT_WARN_MIN ? C.amberLight : NEUTRAL_BG,
                  waited > WAIT_WARN_MIN ? CT.warnInk : C.textSecondary,
                )}>
                  <ClockGlyph />{t('dashboard.queue.waitedShort', { count: waited })}
                </span>
              )}
              {elapsedMin != null && (
                <span style={chipStyle(overChip.bg, overChip.color)}>
                  <ClockGlyph />{formatDuration(t, elapsedMin)}
                </span>
              )}
              {queuePos != null && (
                <span style={chipStyle(
                  queuePos === 1 ? statusColors.arrived.bg : NEUTRAL_BG,
                  queuePos === 1 ? statusColors.arrived.text : C.textSecondary,
                )}>
                  {posLabel(t, queuePos)}
                </span>
              )}
              {clinicName && (
                <span
                  data-testid="reception-row-clinic"
                  style={{
                    ...chipStyle(soft(C.navy, 0.08), C.navyDark),
                    maxWidth: '9rem', overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}
                >
                  {clinicName}
                </span>
              )}
            </div>
          </div>
          {/* «تغییر زمان» (PM-0102) — one 44px control at the row's end rather
              than a second full-width button on every waiting card. Passed only
              for a row that may move. */}
          {apt.status === 'scheduled' && onReschedule && (
            <IconButton
              variant="ghost"
              icon={<CalendarClock size={20} strokeWidth={1.5} aria-hidden />}
              label={s('dashboard.reschedule.action')}
              data-testid={`reception-row-${apt.id}-reschedule`}
              onClick={e => { e.stopPropagation(); onReschedule(); }}
              onKeyDown={e => e.stopPropagation()}
            />
          )}
        </div>
        {apt.status === 'in_visit' && onContinue && (
          <Button
            variant="default" size="sm"
            className="w-full mt-2.5 gap-1.5 min-h-11"
            onClick={e => { e.stopPropagation(); onContinue(); }}
          >
            <Play size={14} strokeWidth={2.6} fill="currentColor" aria-hidden /> {t('dashboard.hero.continueVisit')}
          </Button>
        )}
        {/* The doctor's own booking still awaiting the clinic — theirs to withdraw. */}
        {isOwnPendingRequest(apt) && onWithdraw && (
          <Button
            variant="outline" size="sm"
            className="w-full mt-2.5 min-h-11"
            data-testid={`reception-row-${apt.id}-withdraw`}
            onClick={e => { e.stopPropagation(); onWithdraw(); }}
          >
            {s('dashboard.withdraw.action')}
          </Button>
        )}
      </Card>
    </div>
  );
};

export default MobileAppointmentCard;
